import { useEffect, useState } from "react";
import { VERSAO_SISTEMA_PADRAO } from "../constants/sistema";
import {
  CONFIGURACAO_GRAFICOS_PADRAO,
  type ConfiguracaoGraficos,
  type ConfiguracaoItensGrafico,
  type TipoGrafico,
} from "../api/sistema";

export interface ConfiguracoesSistemaPublicas {
  fantasia: string;
  modulosAtivos: string[];
  loginSocial: boolean;
  sessaoUnica: boolean;
  logoutInatividade: boolean;
  logoutInatividadeMinutos: number;
  cadastroLogin: boolean;
  /** API TEF liberada para a empresa em uso (telas e botões de TEF). */
  tefMenu: boolean;
  /** API FISCAL liberada para a empresa em uso (telas e emissão de NF). */
  fiscalMenu: boolean;
  /** API EMAIL liberada para a empresa em uso. */
  emailApi: boolean;
  /** API WHATSAPP liberada para a empresa em uso. */
  whatsappApi: boolean;
  /** API IA liberada para a empresa em uso. */
  iaApi: boolean;
  /** API CONCILIADOR liberada para a empresa em uso. */
  conciliadorApi: boolean;
  /** Integração de TEF ligada na aba Configuração TEF da empresa. */
  tefIntegracao: boolean;
  /** Integração fiscal ligada na aba Configuração Fiscal da empresa. */
  fiscalIntegracao: boolean;
  /** Integração de e-mail (Resend) ligada na configuração global. */
  emailIntegracao: boolean;
  /** Integração bancária (conciliador) ligada na empresa em uso. */
  conciliadorIntegracao: boolean;
  tourBoasVindas: boolean;
  textoCopyright: string;
  textoVersao: string;
  iaAtiva: boolean;
  /** Efeito de vidro (translúcido + desfoque) nos modais e popups. */
  glass: boolean;
  /** Intensidade do vidro, de 0 (quase opaco) a 100 (bem transparente). */
  glassIntensidade: number;
  configGraficos: ConfiguracaoGraficos;
}

interface ConfiguracoesVisuaisRpc {
  fantasia?: unknown;
  modulosAtivos?: unknown;
  loginSocial?: unknown;
  sessaoUnica?: unknown;
  logoutInatividade?: unknown;
  logoutInatividadeMinutos?: unknown;
  cadastroLogin?: unknown;
  tefMenu?: unknown;
  fiscalMenu?: unknown;
  emailApi?: unknown;
  whatsappApi?: unknown;
  iaApi?: unknown;
  conciliadorApi?: unknown;
  tefIntegracao?: unknown;
  fiscalIntegracao?: unknown;
  emailIntegracao?: unknown;
  conciliadorIntegracao?: unknown;
  tourBoasVindas?: unknown;
  textoCopyright?: unknown;
  textoVersao?: unknown;
  iaAtiva?: unknown;
  glass?: unknown;
  glassIntensidade?: unknown;
  configGraficos?: unknown;
}

const STORAGE_KEY = "i9.configuracoes.publicas";
const STORAGE_FANTASIA_KEY = "fantasiaSistema";
const EVENTO_CONFIGURACOES = "i9:configuracoes-publicas-atualizadas";
export const EVENTO_ASSETS_SISTEMA = "i9:assets-sistema-atualizados";
export const MODULOS_SISTEMA_PADRAO = [
  "b27",
  "b10",
  "b11",
  "b12",
  "b13",
  "b17",
] as const;
const ROTAS_DEPENDEM_TEF = ["/vendas/monitor-tef"] as const;
const ROTAS_DEPENDEM_FISCAL = ["/fiscal", "/vendas/monitor-fiscal", "/vendas/nfes-recebidas"] as const;

const PADRAO_CONFIGURACOES: ConfiguracoesSistemaPublicas = {
  fantasia: "i9 Soluções Digitais",
  modulosAtivos: [...MODULOS_SISTEMA_PADRAO],
  loginSocial: true,
  sessaoUnica: true,
  logoutInatividade: true,
  logoutInatividadeMinutos: 30,
  cadastroLogin: true,
  tefMenu: false,
  fiscalMenu: false,
  emailApi: true,
  whatsappApi: false,
  iaApi: true,
  conciliadorApi: false,
  tefIntegracao: false,
  fiscalIntegracao: false,
  emailIntegracao: true,
  conciliadorIntegracao: false,
  tourBoasVindas: true,
  textoCopyright: `© ${new Date().getFullYear()} I9 Soluções Digitais - Todos os Direitos Reservados.`,
  textoVersao: `Versão ${VERSAO_SISTEMA_PADRAO}`,
  iaAtiva: true,
  glass: false,
  glassIntensidade: 60,
  configGraficos: CONFIGURACAO_GRAFICOS_PADRAO,
};

/** Intensidade do vidro sempre entre 0 e 100; fora disso cai no padrão. */
function normalizarIntensidadeGlass(valor: unknown): number {
  const numero = Number(valor);
  if (!Number.isFinite(numero)) return PADRAO_CONFIGURACOES.glassIntensidade;
  return Math.min(100, Math.max(0, Math.round(numero)));
}

function parseBool(value: unknown, fallback = false): boolean {
  if (value == null || value === "") return fallback;
  return value === true || value === 1 || String(value) === "true" || String(value) === "1";
}

function parseConfigGraficos(value: unknown): ConfiguracaoGraficos {
  const padrao = CONFIGURACAO_GRAFICOS_PADRAO;
  if (!value || typeof value !== "object") return padrao;
  const v = value as Partial<ConfiguracaoGraficos>;
  const tiposLiberados = Array.isArray(v.tiposLiberados)
    ? v.tiposLiberados.filter((tipo) => padrao.tiposLiberados.includes(tipo))
    : [];
  const parseItens = (
    itens: Partial<ConfiguracaoItensGrafico> | undefined,
    fallback: ConfiguracaoItensGrafico
  ): ConfiguracaoItensGrafico => ({
    rotulos: typeof itens?.rotulos === "boolean" ? itens.rotulos : fallback.rotulos,
    legendaPosicao: (["top", "bottom", "left", "right"] as const).includes(itens?.legendaPosicao as never)
      ? (itens?.legendaPosicao as ConfiguracaoItensGrafico["legendaPosicao"])
      : fallback.legendaPosicao,
    marcadores: typeof itens?.marcadores === "boolean" ? itens.marcadores : fallback.marcadores,
  });
  const itens = parseItens(v.itens, padrao.itens);
  const itensPorTipo = Object.fromEntries(
    padrao.tiposLiberados.map((tipo) => [
      tipo,
      parseItens(v.itensPorTipo?.[tipo], itens),
    ])
  ) as Partial<Record<TipoGrafico, ConfiguracaoItensGrafico>>;

  return {
    tiposLiberados: tiposLiberados.length ? tiposLiberados : padrao.tiposLiberados,
    linha: {
      areaPreenchida: typeof v.linha?.areaPreenchida === "boolean" ? v.linha.areaPreenchida : padrao.linha.areaPreenchida,
      gradienteArea: typeof v.linha?.gradienteArea === "boolean" ? v.linha.gradienteArea : padrao.linha.gradienteArea,
      curvaSuave: typeof v.linha?.curvaSuave === "boolean" ? v.linha.curvaSuave : padrao.linha.curvaSuave,
    },
    barra: {
      empilhado: typeof v.barra?.empilhado === "boolean" ? v.barra.empilhado : padrao.barra.empilhado,
      bordaArredondada: typeof v.barra?.bordaArredondada === "boolean" ? v.barra.bordaArredondada : padrao.barra.bordaArredondada,
      orientacao: (["vertical", "horizontal"] as const).includes(v.barra?.orientacao as never) ? (v.barra?.orientacao as ConfiguracaoGraficos["barra"]["orientacao"]) : padrao.barra.orientacao,
    },
    gaugeLiquido: {
      velocidadeOnda: Number.isFinite(Number(v.gaugeLiquido?.velocidadeOnda)) ? Number(v.gaugeLiquido?.velocidadeOnda) : padrao.gaugeLiquido.velocidadeOnda,
      amplitudeOnda: Number.isFinite(Number(v.gaugeLiquido?.amplitudeOnda)) ? Number(v.gaugeLiquido?.amplitudeOnda) : padrao.gaugeLiquido.amplitudeOnda,
      mostrarTexto: typeof v.gaugeLiquido?.mostrarTexto === "boolean" ? v.gaugeLiquido.mostrarTexto : padrao.gaugeLiquido.mostrarTexto,
    },
    itens,
    itensPorTipo,
  };
}

function parseModulos(value: unknown): string[] {
  const itens = String(value ?? "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);

  return Array.from(new Set(itens)).filter((item) => MODULOS_SISTEMA_PADRAO.includes(item as (typeof MODULOS_SISTEMA_PADRAO)[number]));
}

function normalizarFantasia(value: unknown): string {
  const fantasia = String(value ?? "")
    .replace(/[\u0000-\u001F\u007F]/g, "")
    .trim()
    .slice(0, 120);
  const valorInvalido = /^(undefined|null|nan)$/i.test(fantasia);
  return fantasia && !valorInvalido ? fantasia : PADRAO_CONFIGURACOES.fantasia;
}

function aplicarFantasia(configuracoes: ConfiguracoesSistemaPublicas) {
  if (typeof window === "undefined") return;
  const fantasia = normalizarFantasia(configuracoes.fantasia);
  if (document.title !== fantasia) document.title = fantasia;
  try {
    if (localStorage.getItem(STORAGE_FANTASIA_KEY) !== fantasia) {
      localStorage.setItem(STORAGE_FANTASIA_KEY, fantasia);
    }
  } catch {
    /* ignora indisponibilidade do localStorage */
  }
}

function montarConfiguracoes(
  partial?: Partial<ConfiguracoesSistemaPublicas>
): ConfiguracoesSistemaPublicas {
  // Valida também o cache legado para impedir prazos inválidos ou overflow do timer.
  const minutosInatividade = Number(partial?.logoutInatividadeMinutos);
  const modulosAtivos = Array.isArray(partial?.modulosAtivos)
    ? Array.from(new Set(partial.modulosAtivos)).filter((item) =>
        MODULOS_SISTEMA_PADRAO.includes(item as (typeof MODULOS_SISTEMA_PADRAO)[number])
      )
    : [...PADRAO_CONFIGURACOES.modulosAtivos];

  return {
    fantasia: normalizarFantasia(partial?.fantasia),
    modulosAtivos,
    loginSocial: partial?.loginSocial ?? PADRAO_CONFIGURACOES.loginSocial,
    sessaoUnica: partial?.sessaoUnica ?? PADRAO_CONFIGURACOES.sessaoUnica,
    logoutInatividade: partial?.logoutInatividade ?? PADRAO_CONFIGURACOES.logoutInatividade,
    logoutInatividadeMinutos: Number.isInteger(minutosInatividade) && minutosInatividade >= 1 && minutosInatividade <= 1440
      ? minutosInatividade : PADRAO_CONFIGURACOES.logoutInatividadeMinutos,
    cadastroLogin: partial?.cadastroLogin ?? PADRAO_CONFIGURACOES.cadastroLogin,
    tefMenu: partial?.tefMenu ?? PADRAO_CONFIGURACOES.tefMenu,
    fiscalMenu: partial?.fiscalMenu ?? PADRAO_CONFIGURACOES.fiscalMenu,
    emailApi: partial?.emailApi ?? PADRAO_CONFIGURACOES.emailApi,
    whatsappApi: partial?.whatsappApi ?? PADRAO_CONFIGURACOES.whatsappApi,
    iaApi: partial?.iaApi ?? PADRAO_CONFIGURACOES.iaApi,
    conciliadorApi: partial?.conciliadorApi ?? PADRAO_CONFIGURACOES.conciliadorApi,
    tefIntegracao: partial?.tefIntegracao ?? PADRAO_CONFIGURACOES.tefIntegracao,
    fiscalIntegracao: partial?.fiscalIntegracao ?? PADRAO_CONFIGURACOES.fiscalIntegracao,
    emailIntegracao: partial?.emailIntegracao ?? PADRAO_CONFIGURACOES.emailIntegracao,
    conciliadorIntegracao: partial?.conciliadorIntegracao ?? PADRAO_CONFIGURACOES.conciliadorIntegracao,
    tourBoasVindas: partial?.tourBoasVindas ?? PADRAO_CONFIGURACOES.tourBoasVindas,
    textoCopyright: partial?.textoCopyright?.trim() || PADRAO_CONFIGURACOES.textoCopyright,
    textoVersao: partial?.textoVersao?.trim() || PADRAO_CONFIGURACOES.textoVersao,
    iaAtiva: partial?.iaAtiva ?? PADRAO_CONFIGURACOES.iaAtiva,
    glass: partial?.glass ?? PADRAO_CONFIGURACOES.glass,
    glassIntensidade: normalizarIntensidadeGlass(partial?.glassIntensidade),
    configGraficos: parseConfigGraficos(partial?.configGraficos),
  };
}

function dispararAtualizacao(configuracoes: ConfiguracoesSistemaPublicas) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(EVENTO_CONFIGURACOES, { detail: configuracoes }));
}

export function lerConfiguracoesSistemaPublicas(): ConfiguracoesSistemaPublicas {
  if (typeof window === "undefined") return PADRAO_CONFIGURACOES;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return PADRAO_CONFIGURACOES;
    const parsed = JSON.parse(raw) as Partial<ConfiguracoesSistemaPublicas>;
    return montarConfiguracoes(parsed);
  } catch {
    return PADRAO_CONFIGURACOES;
  }
}

export function sincronizarConfiguracoesSistemaPublicas(
  partial: Partial<ConfiguracoesSistemaPublicas>
): ConfiguracoesSistemaPublicas {
  const atual = lerConfiguracoesSistemaPublicas();
  const next = montarConfiguracoes({
    ...atual,
    ...partial,
  });

  if (typeof window !== "undefined") {
    aplicarFantasia(next);
    // A checagem da sessão não deve notificar toda a interface sem uma mudança real.
    if (JSON.stringify(atual) === JSON.stringify(next)) return atual;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
      /* ignora indisponibilidade do localStorage */
    }
    dispararAtualizacao(next);
  }

  return next;
}

let carregamentoConfiguracoesEmAndamento: Promise<ConfiguracoesSistemaPublicas> | null = null;

export function carregarConfiguracoesSistemaPublicas(): Promise<ConfiguracoesSistemaPublicas> {
  if (carregamentoConfiguracoesEmAndamento) return carregamentoConfiguracoesEmAndamento;

  const requisicao = (async () => {
    try {
      const res = await fetch("/api/rpc/obterConfiguracoesVisuais", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        cache: "no-store",
        body: JSON.stringify({ args: [] }),
      });
      if (!res.ok) return lerConfiguracoesSistemaPublicas();

      const dados = (await res.json()) as ConfiguracoesVisuaisRpc | null;
      return sincronizarConfiguracoesSistemaPublicas({
        fantasia: normalizarFantasia(dados?.fantasia),
        modulosAtivos: parseModulos(dados?.modulosAtivos),
        loginSocial: parseBool(dados?.loginSocial, true),
        sessaoUnica: parseBool(dados?.sessaoUnica, true),
        logoutInatividade: parseBool(dados?.logoutInatividade, true),
        logoutInatividadeMinutos: Number(dados?.logoutInatividadeMinutos ?? 30),
        cadastroLogin: parseBool(dados?.cadastroLogin, true),
        tefMenu: parseBool(dados?.tefMenu, false),
        fiscalMenu: parseBool(dados?.fiscalMenu, false),
        emailApi: parseBool(dados?.emailApi, true),
        whatsappApi: parseBool(dados?.whatsappApi, false),
        iaApi: parseBool(dados?.iaApi, true),
        conciliadorApi: parseBool(dados?.conciliadorApi, false),
        tefIntegracao: parseBool(dados?.tefIntegracao, false),
        fiscalIntegracao: parseBool(dados?.fiscalIntegracao, false),
        emailIntegracao: parseBool(dados?.emailIntegracao, true),
        conciliadorIntegracao: parseBool(dados?.conciliadorIntegracao, false),
        tourBoasVindas: parseBool(dados?.tourBoasVindas, true),
        textoCopyright: typeof dados?.textoCopyright === "string" ? dados.textoCopyright : "",
        textoVersao: typeof dados?.textoVersao === "string" ? dados.textoVersao : "",
        iaAtiva: parseBool(dados?.iaAtiva, true),
        glass: parseBool(dados?.glass, false),
        glassIntensidade: normalizarIntensidadeGlass(dados?.glassIntensidade),
        configGraficos: parseConfigGraficos(dados?.configGraficos),
      });
    } catch {
      return lerConfiguracoesSistemaPublicas();
    }
  })();

  carregamentoConfiguracoesEmAndamento = requisicao;
  void requisicao.finally(() => {
    if (carregamentoConfiguracoesEmAndamento === requisicao) {
      carregamentoConfiguracoesEmAndamento = null;
    }
  });
  return requisicao;
}

export function useConfiguracoesSistemaPublicas(): ConfiguracoesSistemaPublicas {
  const [configuracoes, setConfiguracoes] = useState<ConfiguracoesSistemaPublicas>(() =>
    lerConfiguracoesSistemaPublicas()
  );

  useEffect(() => {
    function aplicar(next: ConfiguracoesSistemaPublicas) {
      setConfiguracoes((atual) => JSON.stringify(atual) === JSON.stringify(next) ? atual : next);
    }
    function atualizar() {
      aplicar(lerConfiguracoesSistemaPublicas());
    }
    function atualizarStorage(evento: StorageEvent) {
      // Atividade, favoritos e filtros de outras abas não alteram configurações.
      if (evento.key === STORAGE_KEY || evento.key === null) atualizar();
    }

    void carregarConfiguracoesSistemaPublicas().then(aplicar);
    window.addEventListener(EVENTO_CONFIGURACOES, atualizar as EventListener);
    window.addEventListener("storage", atualizarStorage);

    return () => {
      window.removeEventListener(EVENTO_CONFIGURACOES, atualizar as EventListener);
      window.removeEventListener("storage", atualizarStorage);
    };
  }, []);

  return configuracoes;
}

export function notificarAssetsSistemaAtualizados() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event(EVENTO_ASSETS_SISTEMA));
}

export function rotaLiberadaNasConfiguracoes(
  rota: string,
  tefMenu = true,
  fiscalMenu = true,
  fiscalIntegracao = true
): boolean {
  if (rota.startsWith("/vendas/configuracoes-pdv")) return tefMenu || fiscalMenu;
  if (ROTAS_DEPENDEM_TEF.some((prefixo) => rota.startsWith(prefixo))) return tefMenu;
  if (ROTAS_DEPENDEM_FISCAL.some((prefixo) => rota.startsWith(prefixo))) {
    return fiscalMenu && fiscalIntegracao;
  }
  return true;
}
